import React, { useState } from 'react';
import { 
  Car, 
  Building2, 
  Wind, 
  MapPin, 
  Search, 
  Zap, 
  Check, 
  AlertCircle, 
  X 
} from 'lucide-react';
import { 
  VehicleData, 
  HvacEquipmentData, 
  NicheKey 
} from '../../types';
import { 
  fetchVehicleByPlate, 
  fetchCompanyByCnpj, 
  fetchAddressByCep, 
  searchHvacSpecs, 
  HvacModelSpec,
  VehicleResult 
} from '../../services/publicDataServices';

interface ZeroDigitationWidgetProps {
  selectedNiche: NicheKey;
  vehicleData?: VehicleData;
  setVehicleData: (v: VehicleData) => void;
  hvacData?: HvacEquipmentData;
  setHvacData: (h: HvacEquipmentData) => void;
  setTitle: (t: string | ((prev: string) => string)) => void;
  setClientName: (c: string) => void;
  setClientDocument: (d: string) => void;
  setClientAddress: (a: string) => void;
  setClientPhone: (p: string) => void;
  setTechnicalReport: (r: string | ((prev: string) => string)) => void;
}

export default function ZeroDigitationWidget({
  selectedNiche,
  vehicleData,
  setVehicleData,
  hvacData,
  setHvacData,
  setTitle,
  setClientName,
  setClientDocument,
  setClientAddress,
  setClientPhone,
  setTechnicalReport
}: ZeroDigitationWidgetProps) {
  const [plateInput, setPlateInput] = useState('');
  const [isSearchingPlate, setIsSearchingPlate] = useState(false);
  const [cnpjInput, setCnpjInput] = useState('');
  const [isSearchingCnpj, setIsSearchingCnpj] = useState(false);
  const [cepInput, setCepInput] = useState('');
  const [isSearchingCep, setIsSearchingCep] = useState(false);
  const [zeroMessage, setZeroMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [isHvacModalOpen, setIsHvacModalOpen] = useState(false);
  const [hvacSearchTerm, setHvacSearchTerm] = useState('');

  // 1. Busca por Placa Veicular
  const handleSearchPlate = async () => {
    if (!plateInput.trim()) return;
    setIsSearchingPlate(true);
    setZeroMessage(null);

    const res: VehicleResult = await fetchVehicleByPlate(plateInput);
    setIsSearchingPlate(false);

    if (res.success) {
      const vData: VehicleData = {
        plate: res.formattedPlate || plateInput.toUpperCase(),
        brand: res.brand || '',
        model: res.model || '',
        version: res.version,
        modelYear: res.modelYear || 2022,
        manufacturingYear: res.manufacturingYear,
        color: res.color,
        fuel: res.fuel,
        engine: res.engine,
        chassiFinal: res.chassiFinal,
        fipeValueEstimate: res.fipeValueEstimate
      };

      setVehicleData(vData);
      setTitle(`OS Mecânica / Auto Center - ${vData.brand} ${vData.model} (${vData.plate})`);
      setTechnicalReport(prev => 
        prev ? `${prev}\n\n[DADOS VEÍCULO]: ${vData.brand} ${vData.model} | Ano: ${vData.modelYear} | Cor: ${vData.color || 'N/I'} | Motor: ${vData.engine || 'Flex'}`
        : `[DADOS DO VEÍCULO]:\n• Modelo: ${vData.brand} ${vData.model} (${vData.modelYear})\n• Placa: ${vData.plate}\n• Motorização: ${vData.engine || 'Padrão'}\n• Tabela FIPE Aprox: R$ ${vData.fipeValueEstimate?.toLocaleString('pt-BR') || 'Consulte'}`
      );

      setZeroMessage({
        type: 'success',
        text: res.isMocked 
          ? `[SIMULAÇÃO EM DESENVOLVIMENTO] Veículo gerado para testes: ${vData.brand} ${vData.model} ${vData.modelYear}.`
          : `Veículo localizado: ${vData.brand} ${vData.model} ${vData.modelYear} (${vData.color || ''})! Dados preenchidos.`
      });
      setPlateInput('');
    } else {
      setZeroMessage({
        type: 'error',
        text: res.error || 'Não foi possível localizar dados para esta placa.'
      });
    }
  };

  // 2. Busca por CNPJ
  const handleSearchCnpj = async () => {
    if (!cnpjInput.trim()) return;
    setIsSearchingCnpj(true);
    setZeroMessage(null);

    const res = await fetchCompanyByCnpj(cnpjInput);
    setIsSearchingCnpj(false);

    if (res.success) {
      setClientName(res.nomeFantasia || res.razaoSocial);
      setClientDocument(res.cnpj);
      setClientAddress(res.formattedAddress);
      if (res.phone) setClientPhone(res.phone);

      setZeroMessage({
        type: 'success',
        text: `Empresa localizada: ${res.nomeFantasia || res.razaoSocial}! Razão Social, CNPJ e Endereço preenchidos.`
      });
      setCnpjInput('');
    } else {
      setZeroMessage({
        type: 'error',
        text: res.error || 'CNPJ não localizado. Verifique se digitou os números corretamente.'
      });
    }
  };

  // 3. Busca por CEP
  const handleSearchCep = async () => {
    if (!cepInput.trim()) return;
    setIsSearchingCep(true);
    setZeroMessage(null);

    const res = await fetchAddressByCep(cepInput);
    setIsSearchingCep(false);

    if (res.success) {
      setClientAddress(res.formattedAddress);
      setZeroMessage({
        type: 'success',
        text: `Endereço localizado: ${res.formattedAddress}`
      });
      setCepInput('');
    } else {
      setZeroMessage({
        type: 'error',
        text: res.error || 'CEP não encontrado.'
      });
    }
  };

  // 4. Seleção de Modelo HVAC
  const handleSelectHvacModel = (spec: HvacModelSpec) => {
    const hData: HvacEquipmentData = {
      brand: spec.brand,
      modelName: spec.modelName,
      btus: spec.btus,
      gasType: spec.gasType,
      factoryGasChargeGrams: spec.factoryGasChargeGrams,
      voltage: spec.voltage,
      inverter: spec.inverter
    };

    setHvacData(hData);
    setTitle(`Instalação / Manutenção Split - ${spec.brand} ${spec.btus} BTUs (${spec.gasType})`);
    setTechnicalReport(prev => 
      prev ? `${prev}\n\n[EQUIPAMENTO CLIMATIZAÇÃO]: ${spec.brand} ${spec.modelName} ${spec.btus} BTUs | Gás: ${spec.gasType} (${spec.factoryGasChargeGrams}g)`
      : `[EQUIPAMENTO CLIMATIZAÇÃO]:\n• Marca/Modelo: ${spec.brand} ${spec.modelName}\n• Capacidade: ${spec.btus.toLocaleString('pt-BR')} BTUs\n• Fluido Refrigerante: ${spec.gasType}\n• Carga de Fábrica: ${spec.factoryGasChargeGrams}g\n• Tensão: ${spec.voltage}`
    );

    setIsHvacModalOpen(false);
    setZeroMessage({
      type: 'success',
      text: `Ar-condicionado ${spec.brand} ${spec.btus} BTUs (${spec.gasType} - ${spec.factoryGasChargeGrams}g) selecionado!`
    });
  };

  const isAutomotive = selectedNiche === 'mecanica';
  const isHvac = selectedNiche === 'hvac';

  const subtitleText = isAutomotive
    ? 'Digite a Placa, CNPJ ou CEP para preencher os dados da OS automaticamente.'
    : isHvac
    ? 'Selecione o Modelo de Ar-Condicionado, CNPJ ou CEP para preencher a OS.'
    : 'Informe o CEP ou CNPJ para preencher o endereço e dados da empresa automaticamente.';

  return (
    <div className="bg-neutral-950 text-white p-4 sm:p-5 rounded-3xl border border-neutral-800 shadow-md space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-brand/20 text-brand-light flex items-center justify-center font-bold shrink-0">
            <Zap className="h-4 w-4 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-xs sm:text-sm font-black text-white">
              Preenchimento Rápido dos Dados da OS
            </h3>
            <p className="text-[11px] text-zinc-400">
              {subtitleText}
            </p>
          </div>
        </div>
      </div>

      {/* Grid de Inputs de Auto-Preenchimento Dinâmico por Nicho */}
      <div className={`grid grid-cols-1 ${isAutomotive || isHvac ? 'sm:grid-cols-2 md:grid-cols-3' : 'sm:grid-cols-2'} gap-2.5 pt-1`}>
        
        {/* 1. Placa do Veículo - EXCLUSIVO PARA MECÂNICA / AUTO CENTERS */}
        {isAutomotive && (
          <div className="bg-white/5 p-2.5 rounded-2xl border border-white/10 flex flex-col justify-between gap-1.5">
            <label className="text-[10px] font-black uppercase tracking-wider text-emerald-400 flex items-center gap-1">
              <Car className="h-3.5 w-3.5" />
              <span>1. Placa do Veículo</span>
            </label>
            <div className="flex items-center gap-1">
              <input
                type="text"
                placeholder="Ex: BRA2E19"
                value={plateInput}
                onChange={e => setPlateInput(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && handleSearchPlate()}
                className="w-full bg-white text-zinc-900 font-mono font-black text-xs px-2.5 py-1.5 rounded-xl uppercase placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/30"
              />
              <button
                type="button"
                onClick={handleSearchPlate}
                disabled={isSearchingPlate}
                className="px-2.5 py-1.5 bg-brand hover:bg-brand-dark text-white font-black text-xs rounded-xl cursor-pointer transition-all shrink-0 active:scale-95"
                title="Buscar dados do veículo"
              >
                {isSearchingPlate ? '...' : <Search className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        )}

        {/* 2. Catálogo Técnico Ar-Condicionado - EXCLUSIVO PARA HVAC / CLIMATIZAÇÃO */}
        {isHvac && (
          <div className="bg-white/5 p-2.5 rounded-2xl border border-white/10 flex flex-col justify-between gap-1.5">
            <label className="text-[10px] font-black uppercase tracking-wider text-emerald-400 flex items-center gap-1">
              <Wind className="h-3.5 w-3.5" />
              <span>1. Modelo Ar-Condicionado</span>
            </label>
            <button
              type="button"
              onClick={() => setIsHvacModalOpen(true)}
              className="w-full py-2 bg-white text-zinc-900 font-bold text-xs px-2.5 rounded-xl flex items-center justify-between hover:bg-zinc-100 transition-colors cursor-pointer active:scale-95"
            >
              <span className="truncate">
                {hvacData ? `${hvacData.brand} ${hvacData.btus} BTUs` : 'Selecionar Modelo...'}
              </span>
              <Search className="h-3 w-3 text-zinc-500 shrink-0" />
            </button>
          </div>
        )}

        {/* 3. Busca de Endereço por CEP (Todos os nichos) */}
        <div className="bg-white/5 p-2.5 rounded-2xl border border-white/10 flex flex-col justify-between gap-1.5">
          <label className="text-[10px] font-black uppercase tracking-wider text-emerald-400 flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" />
            <span>{isAutomotive || isHvac ? '2. Endereço por CEP' : '1. Endereço da Obra / Local (CEP)'}</span>
          </label>
          <div className="flex items-center gap-1">
            <input
              type="text"
              placeholder="01310-100"
              value={cepInput}
              onChange={e => setCepInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearchCep()}
              className="w-full bg-white text-zinc-900 font-mono font-bold text-xs px-2.5 py-1.5 rounded-xl placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
            <button
              type="button"
              onClick={handleSearchCep}
              disabled={isSearchingCep}
              className="px-2.5 py-1.5 bg-brand hover:bg-brand-dark text-white font-black text-xs rounded-xl cursor-pointer transition-all shrink-0 active:scale-95"
              title="Buscar CEP do endereço"
            >
              {isSearchingCep ? '...' : <Search className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        {/* 4. CNPJ de Empresas (Para serviços corporativos/B2B em qualquer nicho) */}
        <div className="bg-white/5 p-2.5 rounded-2xl border border-white/10 flex flex-col justify-between gap-1.5">
          <label className="text-[10px] font-black uppercase tracking-wider text-emerald-400 flex items-center gap-1">
            <Building2 className="h-3.5 w-3.5" />
            <span>{isAutomotive || isHvac ? '3. CNPJ da Empresa' : '2. Dados da Empresa (CNPJ)'}</span>
          </label>
          <div className="flex items-center gap-1">
            <input
              type="text"
              placeholder="Ex: 12.345.678/0001-90"
              value={cnpjInput}
              onChange={e => setCnpjInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearchCnpj()}
              className="w-full bg-white text-zinc-900 font-mono font-bold text-xs px-2.5 py-1.5 rounded-xl placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/30"
            />
            <button
              type="button"
              onClick={handleSearchCnpj}
              disabled={isSearchingCnpj}
              className="px-2.5 py-1.5 bg-brand hover:bg-brand-dark text-white font-black text-xs rounded-xl cursor-pointer transition-all shrink-0 active:scale-95"
              title="Buscar dados pelo CNPJ"
            >
              {isSearchingCnpj ? '...' : <Search className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

      </div>

      {/* Banner de Feedback da Busca */}
      {zeroMessage && (
        <div className={`p-2.5 rounded-xl text-xs flex items-center justify-between gap-2 ${
          zeroMessage.type === 'success' 
            ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40' 
            : 'bg-rose-500/20 text-rose-200 border border-rose-500/40'
        }`}>
          <div className="flex items-center gap-2">
            {zeroMessage.type === 'success' ? <Check className="h-4 w-4 text-emerald-400 shrink-0" /> : <AlertCircle className="h-4 w-4 text-rose-400 shrink-0" />}
            <span className="font-bold">{zeroMessage.text}</span>
          </div>
          <button 
            type="button"
            onClick={() => setZeroMessage(null)}
            className="text-white/60 hover:text-white"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Modal Técnico de Ar-Condicionado / HVAC */}
      {isHvacModalOpen && (
        <div className="fixed inset-0 z-100 bg-neutral-950/85 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-3xl p-6 space-y-4 shadow-2xl border border-zinc-100 text-zinc-900 text-xs">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
              <div>
                <h3 className="text-sm font-black text-zinc-900 flex items-center gap-1.5">
                  <Wind className="h-4 w-4 text-brand" />
                  Catálogo Técnico de Climatização (HVAC)
                </h3>
                <p className="text-[11px] text-zinc-500">Selecione o modelo para carregar tipo e gramas de fluido refrigerante.</p>
              </div>
              <button 
                type="button"
                onClick={() => setIsHvacModalOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full hover:bg-zinc-100 cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
              <input
                type="text"
                placeholder="Buscar por marca ou modelo (ex: LG Dual Inverter, Midea 12000, Daikin)..."
                value={hvacSearchTerm}
                onChange={e => setHvacSearchTerm(e.target.value)}
                className="w-full bg-zinc-50 border border-zinc-100 rounded-xl pl-9 pr-3 py-2.5 text-xs text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
              />
            </div>

            <div className="max-h-60 overflow-y-auto space-y-1.5 pr-1">
              {searchHvacSpecs(hvacSearchTerm).map((spec, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSelectHvacModel(spec)}
                  className="p-3 bg-zinc-50 hover:bg-emerald-50/60 border border-zinc-100 hover:border-emerald-200 rounded-2xl flex items-center justify-between cursor-pointer transition-all active:scale-98"
                >
                  <div>
                    <h4 className="font-bold text-zinc-900">{spec.brand} {spec.modelName}</h4>
                    <p className="text-[10px] text-zinc-500 font-medium">
                      Capacidade: <strong>{spec.btus.toLocaleString('pt-BR')} BTUs</strong> • Tensão: {spec.voltage}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-mono font-bold bg-emerald-50 text-brand border border-emerald-100 px-2 py-0.5 rounded-md">
                      {spec.gasType}
                    </span>
                    <span className="block text-[9px] text-zinc-400 font-bold mt-0.5">
                      Carga: {spec.factoryGasChargeGrams}g
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
